// /admin/reports · monthly completion grid. Per-staff per-month cells show
// "completed / due" and an on-time % colored band. Last column is current
// month, oldest is leftmost. Bottom row = company totals.
//
// Refresh button reloads from the worker (queries are derived live, no
// pre-aggregation table). Default 6 months, selectable 3 / 6 / 12.

import { useState, useEffect } from 'react';
import * as api from '../lib/api.ts';
import { Avatar } from '../components/Avatar.jsx';
import { navigate } from '../lib/routing.js';
import { isElevated } from '../lib/auth.ts';

function pctClass(pct) {
  if (pct === null) return '';
  if (pct >= 80) return 'pct-green';
  if (pct >= 50) return 'pct-amber';
  return 'pct-red';
}

function fmtMonth(m) {
  // "2026-05" → "5月"
  const [, mm] = m.split('-');
  return `${parseInt(mm, 10)}月`;
}

function cellPct(cell) {
  if (cell.due === 0) return null;
  return Math.round((cell.on_time / cell.due) * 100);
}

export const AdminReports = ({ user, staff = [] }) => {
  const [report, setReport] = useState(null);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr('');
    api.loadMonthlyReport(months)
      .then(r => { if (!cancelled) { setReport(r); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e.message || '加载失败'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [months, refreshKey]);

  if (!isElevated(user)) {
    return <div className="vava-card empty-state">仅管理员</div>;
  }
  if (loading) return <div className="vava-card empty-state">加载中…</div>;
  if (err) return <div className="vava-card empty-state">{err}</div>;
  if (!report) return null;

  const personOf = (id) => staff.find(s => s.id === id);
  const monthLabels = report.months.map(fmtMonth);
  const currentMonth = report.months[report.months.length - 1];

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">月度报告</h1>
          <div className="vava-page-meta">
            按主负责（R）维度统计 · 单元格 = 完成 / 应到期 (按时率) ·
            数据刷新于 <span className="mono">{new Date(report.generated_at).toLocaleString('zh-CN', { hour12: false })}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="vava-select" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            <option value="3">近 3 月</option>
            <option value="6">近 6 月</option>
            <option value="12">近 12 月</option>
          </select>
          <button className="btn btn-secondary" onClick={() => setRefreshKey(k => k + 1)}>
            ⟳ 刷新
          </button>
        </div>
      </div>

      <div className="reports-grid-wrap">
        <table className="reports-grid">
          <thead>
            <tr>
              <th className="reports-staff-col">员工</th>
              {monthLabels.map((label, i) => (
                <th key={i} className={`reports-month-col ${report.months[i] === currentMonth ? 'current' : ''}`}>
                  {label}
                  {report.months[i] === currentMonth && <span className="reports-current-tag">本月</span>}
                </th>
              ))}
              <th className="reports-total-col">{months} 月合计</th>
            </tr>
          </thead>
          <tbody>
            {report.per_staff.map(row => {
              const totalCompleted = row.months.reduce((a, m) => a + m.completed, 0);
              const totalDue = row.months.reduce((a, m) => a + m.due, 0);
              const totalOnTime = row.months.reduce((a, m) => a + m.on_time, 0);
              const totalPct = totalDue > 0 ? Math.round((totalOnTime / totalDue) * 100) : null;
              const personRec = personOf(row.staff_id);
              return (
                <tr key={row.staff_id} onClick={() => navigate('/people/' + row.staff_id)}
                    style={{ cursor: 'pointer' }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar staff={personRec || { id: row.staff_id, name: row.name, title: row.title }} />
                      <div>
                        <div className="staff-name">{row.name}</div>
                        <div className="staff-title">{row.title}</div>
                      </div>
                    </div>
                  </td>
                  {row.months.map(c => {
                    const pct = cellPct(c);
                    const isCurrent = c.month === currentMonth;
                    return (
                      <td key={c.month} className={`reports-cell ${isCurrent ? 'current' : ''}`}>
                        <div className={`reports-cell-pct ${pctClass(pct)}`}>
                          {pct !== null ? `${pct}%` : '—'}
                        </div>
                        <div className="reports-cell-counts mono">
                          {c.completed}/{c.due || '—'}
                        </div>
                      </td>
                    );
                  })}
                  <td className="reports-cell reports-total">
                    <div className={`reports-cell-pct ${pctClass(totalPct)}`}>
                      {totalPct !== null ? `${totalPct}%` : '—'}
                    </div>
                    <div className="reports-cell-counts mono">
                      {totalCompleted}/{totalDue || '—'}
                    </div>
                  </td>
                </tr>
              );
            })}
            {/* Company totals */}
            <tr className="reports-company-row">
              <td><strong>全员合计</strong></td>
              {report.company.map(c => {
                const pct = cellPct(c);
                return (
                  <td key={c.month} className="reports-cell">
                    <div className={`reports-cell-pct ${pctClass(pct)}`}>
                      {pct !== null ? `${pct}%` : '—'}
                    </div>
                    <div className="reports-cell-counts mono">
                      {c.completed}/{c.due || '—'}
                    </div>
                  </td>
                );
              })}
              <td className="reports-cell reports-total">
                {(() => {
                  const tc = report.company.reduce((a, m) => a + m.completed, 0);
                  const td = report.company.reduce((a, m) => a + m.due, 0);
                  const to = report.company.reduce((a, m) => a + m.on_time, 0);
                  const pct = td > 0 ? Math.round((to / td) * 100) : null;
                  return (
                    <>
                      <div className={`reports-cell-pct ${pctClass(pct)}`}>{pct !== null ? `${pct}%` : '—'}</div>
                      <div className="reports-cell-counts mono">{tc}/{td || '—'}</div>
                    </>
                  );
                })()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="reports-legend">
        <span className="reports-legend-pill pct-green">≥80%</span>
        <span className="reports-legend-pill pct-amber">50-79%</span>
        <span className="reports-legend-pill pct-red">&lt;50%</span>
        <span style={{ color: 'var(--vava-text-subtle)' }}>
          % = 按时完成的到期待办 / 当月到期待办. 完成 / 到期 = 当月完成待办数 / 当月到期待办数 (按 R 负责)
        </span>
      </div>
    </>
  );
};
